import { PASSWORD_REGEX, wrongPasswordMessage } from '@/constants/regex';
import { useNewSellerPasswordReset } from '@/hooks/sellers/useGetSellers/useSellerNewPasswordReset';
import { WritableDraft } from 'immer';
import { ChangeEvent, useState } from 'react';
import { useImmer } from 'use-immer';
import {
  NewPasswordFormUi,
  initialNewPasswordFormUi,
} from './NewPasswordFormUi';

const unmatchedPasswordMeessage = 'Las contraseñas ingresadas no coinciden.';

export function useNewPasswordFormViewController() {
  const [uiState, updateUiState] = useImmer<NewPasswordFormUi>(
    initialNewPasswordFormUi
  );

  const { resetPassword, isSending } = useNewSellerPasswordReset();

  //To allow display errors on confirmPassword, only if the user started typing on it
  const [confirmPasswordChanged, setConfirmPasswordChanged] = useState(false);

  const onPasswordChanged = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) =>
    updateUiState((draft) => {
      draft.password = e.target.value;
      calculatePasswordErrorMessage(draft);
      if (confirmPasswordChanged) calculateConfirmedPasswordErrorMessage(draft);
      calculateButtonState(draft);
    });

  const onConfirmedPasswordChanged = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setConfirmPasswordChanged(true);
    updateUiState((draft) => {
      draft.confirmedPassword = e.target.value;
      calculatePasswordErrorMessage(draft);
      calculateConfirmedPasswordErrorMessage(draft);
      calculateButtonState(draft);
    });
  };

  const calculatePasswordErrorMessage = (
    draft: WritableDraft<NewPasswordFormUi>
  ) => {
    draft.passwordError = PASSWORD_REGEX.test(draft.password)
      ? undefined
      : wrongPasswordMessage;
  };

  const calculateConfirmedPasswordErrorMessage = (
    draft: WritableDraft<NewPasswordFormUi>
  ) => {
    if (draft.confirmedPassword !== draft.password) {
      draft.confirmedPasswordError = unmatchedPasswordMeessage;
    } else {
      draft.confirmedPasswordError = PASSWORD_REGEX.test(draft.password)
        ? undefined
        : wrongPasswordMessage;
    }
  };

  const calculateButtonState = (draft: WritableDraft<NewPasswordFormUi>) => {
    draft.isButtonDisabled =
      draft.password !== draft.confirmedPassword ||
      !PASSWORD_REGEX.test(draft.password);
  };

  const togglePasswordVisibility = () =>
    updateUiState((state) => {
      state.showPassword = !state.showPassword;
    });

  const toggleConfirmPasswordVisibility = () =>
    updateUiState((state) => {
      state.showConfirmPassword = !state.showConfirmPassword;
    });

  const handleSubmit = () => {
    if (uiState.isButtonDisabled) return;

    const currentPassword = uiState.password;
    resetPassword(
      { password: currentPassword },
      {
        onSuccess: () =>
          updateUiState((draft) => {
            draft.isPasswordSubmitted = true;
          }),
      }
    );
  };

  return {
    uiState,
    isSending,
    actions: {
      onPasswordChanged,
      onConfirmedPasswordChanged,
      togglePasswordVisibility,
      toggleConfirmPasswordVisibility,
      handleSubmit,
    },
  };
}
